import { useCallback, useEffect, useRef, useState } from "react";
import { forexApi } from "@/lib/forex-api";
import type { ForexOperationsOverviewAgentPage, ForexOperationsOverviewCore, ForexOperationsOverviewCoverage, ForexOperationsOverviewEquity, ForexOperationsOverviewRuntime, ForexOperationsOverviewPerformance, ForexOperationsOverviewFollowObserved, ForexOperationsOverviewDecisionObserved, ForexOperationsOverviewMarketObserved, ForexOperationsOverviewExecutionObserved, ForexOperatorTodoPage } from "@/lib/forex-api";
import ForexOperationsOverviewView from "./ForexOperationsOverviewView";
import { operationsOverviewFromCore, operationsOverviewPlaceholder, operationsOverviewWithEquity, operationsOverviewWithRuntime, operationsOverviewWithPerformance, operationsOverviewWithFollowObserved, operationsOverviewWithDecisionObserved, operationsOverviewWithMarketObserved, operationsOverviewWithExecutionObserved } from "./core-adapter";
import type { OverviewRange, OverviewSection } from "./types";

const staleSection = <T,>(section: OverviewSection<T>): OverviewSection<T> =>
  section.data !== null && ["ready", "partial", "empty"].includes(section.state) ? { ...section, state: "stale" } : section;

interface Props {
  environmentLabel: string;
  onNavigate?: (target: string) => void;
}

/** Read-only overview. Independent detail requests may fail without hiding the core facts. */
export default function ForexOperationsOverviewContainer({ environmentLabel, onNavigate }: Props) {
  const [range, setRange] = useState<OverviewRange>("7d");
  const [accountEnvironment, setAccountEnvironment] = useState<"simulation" | "real">("simulation");
  const [selectedCurrency, setSelectedCurrency] = useState<string>();
  const [core, setCore] = useState<ForexOperationsOverviewCore | null>(null);
  const [agentPage, setAgentPage] = useState<ForexOperationsOverviewAgentPage | null>(null);
  const [todoPage, setTodoPage] = useState<ForexOperatorTodoPage | null>(null);
  const [equity, setEquity] = useState<ForexOperationsOverviewEquity | null>(null);
  const [runtime, setRuntime] = useState<ForexOperationsOverviewRuntime | null>(null);
  const [performance, setPerformance] = useState<ForexOperationsOverviewPerformance | null>(null);
  const [followObserved, setFollowObserved] = useState<ForexOperationsOverviewFollowObserved | null>(null);
  const [decisionObserved, setDecisionObserved] = useState<ForexOperationsOverviewDecisionObserved | null>(null);
  const [marketObserved, setMarketObserved] = useState<ForexOperationsOverviewMarketObserved | null>(null);
  const [executionObserved, setExecutionObserved] = useState<ForexOperationsOverviewExecutionObserved | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "offline" | "forbidden">("loading");
  const [refreshing, setRefreshing] = useState(false);
  const requestId = useRef(0);
  const skipAutomaticCurrencyReload = useRef<{ environment: "simulation" | "real"; range: OverviewRange; currency?: string } | null>(null);

  const load = useCallback(async (refresh: boolean) => {
    const current = ++requestId.current;
    if (refresh) setRefreshing(true);
    else setState("loading");
    try {
      const coreRequest = forexApi.getOperationsOverviewCore();
      // The scale cards do not wait for slower Broker/history or runtime reads.
      void coreRequest.then((value) => {
        if (current === requestId.current) { setCore(value); setState("ready"); }
      }, (error: unknown) => {
        if (current !== requestId.current) return;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) { setCore(null); setState("forbidden"); }
        else setState(typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "error");
      });
      const equityRequest = forexApi.getOperationsOverviewEquity(accountEnvironment);
      const performanceRequest = equityRequest.then((value) => {
        const currencies = value?.currencies?.map((item) => item.currency) ?? [];
        const chosen = currencies.includes(selectedCurrency ?? "") ? selectedCurrency : currencies[0];
        return forexApi.getOperationsOverviewPerformance(range, accountEnvironment, chosen);
      }, () => forexApi.getOperationsOverviewPerformance(range, accountEnvironment));
      const [coreResult, agentResult, todoResult, equityResult, runtimeResult, followResult, decisionResult, marketResult, executionResult, performanceResult] = await Promise.allSettled([
        coreRequest, forexApi.getOperationsOverviewAgents(), forexApi.getOperationsOverviewTodos(),
        equityRequest, forexApi.getOperationsOverviewRuntime(accountEnvironment),
        forexApi.getOperationsOverviewFollowObserved(range), forexApi.getOperationsOverviewDecisionObserved(),
        forexApi.getOperationsOverviewMarketObserved(), forexApi.getOperationsOverviewExecutionObserved(), performanceRequest,
      ]);
      if (current !== requestId.current) return;
      const currencies = equityResult.status === "fulfilled" ? equityResult.value?.currencies?.map((item) => item.currency) ?? [] : [];
      const effectiveCurrency = currencies.includes(selectedCurrency ?? "") ? selectedCurrency : currencies[0];
      const forbidden = [coreResult, agentResult, todoResult, equityResult, runtimeResult, followResult, decisionResult, marketResult, executionResult, performanceResult].some((result) => result.status === "rejected" &&
        [401, 403].includes((result.reason as { response?: { status?: number } })?.response?.status ?? 0));
      if (forbidden) {
        setCore(null); setAgentPage(null); setTodoPage(null); setEquity(null); setRuntime(null); setPerformance(null); setFollowObserved(null); setDecisionObserved(null); setMarketObserved(null); setExecutionObserved(null); setState("forbidden");
        return;
      }
      if (coreResult.status === "rejected") throw coreResult.reason;
      setCore(coreResult.value);
      setAgentPage(agentResult.status === "fulfilled" ? agentResult.value : null);
      setTodoPage(todoResult.status === "fulfilled" ? todoResult.value : null);
      setEquity(equityResult.status === "fulfilled" && equityResult.value?.account_environment === accountEnvironment
        ? equityResult.value : null);
      setRuntime(runtimeResult.status === "fulfilled" && runtimeResult.value?.account_environment === accountEnvironment
        ? runtimeResult.value : null);
      setPerformance(performanceResult.status === "fulfilled" && performanceResult.value?.account_environment === accountEnvironment
        ? performanceResult.value : null);
      setFollowObserved(followResult.status === "fulfilled" && followResult.value?.range === range
        ? followResult.value : null);
      setDecisionObserved(decisionResult.status === "fulfilled" ? decisionResult.value : null);
      setMarketObserved(marketResult.status === "fulfilled" ? marketResult.value : null);
      setExecutionObserved(executionResult.status === "fulfilled" ? executionResult.value : null);
      if (effectiveCurrency !== selectedCurrency) {
        skipAutomaticCurrencyReload.current = { environment: accountEnvironment, range, currency: effectiveCurrency };
        setSelectedCurrency(effectiveCurrency);
      }
      setState("ready");
    } catch (error) {
      if (current !== requestId.current) return;
      const status = (error as { response?: { status?: number } })?.response?.status;
      const next = status === 401 || status === 403 ? "forbidden" : typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "error";
      if (next === "forbidden") setCore(null);
      setState(next);
    } finally {
      if (current === requestId.current) setRefreshing(false);
    }
  }, [accountEnvironment, range, selectedCurrency]);

  const loadEquityCoverage = useCallback(async (snapshotHash: string, cursor?: string): Promise<ForexOperationsOverviewCoverage> => {
    if (!equity || equity.account_environment !== accountEnvironment || equity.snapshot_hash !== snapshotHash) {
      throw new Error("OVERVIEW_COVERAGE_SNAPSHOT_CHANGED");
    }
    try {
      return await forexApi.getOperationsOverviewCoverage(accountEnvironment, snapshotHash, cursor);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) {
        requestId.current += 1;
        setCore(null); setAgentPage(null); setTodoPage(null); setEquity(null); setRuntime(null);
        setPerformance(null); setFollowObserved(null); setDecisionObserved(null);
        setMarketObserved(null); setExecutionObserved(null); setState("forbidden");
      }
      throw error;
    }
  }, [accountEnvironment, equity]);

  useEffect(() => {
    const automatic = skipAutomaticCurrencyReload.current;
    skipAutomaticCurrencyReload.current = null;
    if (automatic?.environment === accountEnvironment && automatic.range === range && automatic.currency === selectedCurrency) {
      return;
    }
    setEquity(null);
    setRuntime(null);
    setPerformance(null);
    setFollowObserved(null);
    setDecisionObserved(null);
    setMarketObserved(null);
    setExecutionObserved(null);
    void load(false);
    return () => { requestId.current += 1; };
  }, [load, accountEnvironment, range, selectedCurrency]);

  const historical = core && state !== "forbidden" ? operationsOverviewWithExecutionObserved(operationsOverviewWithMarketObserved(operationsOverviewWithDecisionObserved(operationsOverviewWithFollowObserved(
    operationsOverviewWithPerformance(
      operationsOverviewWithRuntime(
        operationsOverviewWithEquity(operationsOverviewFromCore(core, range, environmentLabel, agentPage, todoPage),
          equity, accountEnvironment, selectedCurrency), runtime, accountEnvironment),
      performance, accountEnvironment, range),
    followObserved, range,
  ), decisionObserved), marketObserved), executionObserved) : null;
  const model = historical && (state === "error" || state === "offline")
    ? { ...historical,
      metrics: historical.metrics.map((metric) => ["ready", "partial", "empty"].includes(metric.state) ? { ...metric, state: "stale" as const } : metric),
      briefing: historical.briefing ? staleSection(historical.briefing) : undefined,
      userTrend: staleSection(historical.userTrend), tradeTrend: staleSection(historical.tradeTrend),
      runtime: staleSection(historical.runtime), anomalies: staleSection(historical.anomalies),
      tasks: staleSection(historical.tasks), agents: staleSection(historical.agents),
    }
    : historical ?? operationsOverviewPlaceholder(state === "ready" ? "loading" : state, range, environmentLabel);

  return <>
    {historical && (state === "error" || state === "offline") && <p role="alert" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      {state === "offline" ? "连接已断开" : "刷新失败"}，当前显示上次取得的数据；请检查更新时间后重试。
    </p>}
    <ForexOperationsOverviewView model={model} refreshing={refreshing}
      onRangeChange={setRange} onRefresh={() => void load(true)} onNavigate={onNavigate}
      onLoadEquityCoverage={loadEquityCoverage}
      onAccountEnvironmentChange={setAccountEnvironment} onCurrencyChange={setSelectedCurrency} />
  </>;
}
